import * as React from 'react';

import styled, { css } from 'styled-components';

import Flex from '@components/layout/flex';
import { motion } from 'framer-motion';
import { easeTransition } from '@components/framer';
import { Typography } from '../typography';

interface SwitchProps {
  className?: string;
  active?: boolean;
  label?: React.ReactNode;
  inactiveText?: React.ReactNode;
  activeText?: React.ReactNode;
  isGreen?: boolean;
  isBig?: boolean;
  onClick?: () => void;
}

const Switch: React.FC<SwitchProps> = ({
  className,
  active,
  label,
  inactiveText,
  activeText,
  isGreen,
  isBig,
  onClick,
}) => {
  const Circle = {
    rest: {
      right: 'auto',
      left: isBig ? 5 : 4,
    },
    animate: {
      right: isBig ? 5 : 4,
      left: 'auto',
      transition: {
        ...easeTransition,
      },
    },
  };

  const Text = {
    rest: {
      right: isBig ? 10 : 6,
      left: 'auto',
      opacity: ['100%', '0%', '100%'],
    },
    animate: {
      right: 'auto',
      left: isBig ? 10 : 6,
      opacity: ['100%', '0%', '100%'],
      transition: {
        ...easeTransition,
      },
    },
  };
  return (
    <SwitchWrapper className={className}>
      {label && <SwitchLabel>{label}</SwitchLabel>}
      <SwitchContainer
        active={active}
        isBig={isBig}
        className={isGreen ? 'green-bg' : ''}
        onClick={() => (onClick ? onClick() : null)}
      >
        <SwitchItem
          variants={Circle}
          initial="rest"
          animate={active ? 'animate' : undefined}
          active={active}
          isGreen={isGreen}
          isBig={isBig}
        />
        <SwitchTypography
          className={isGreen ? 'green-text' : ''}
          active={active}
          variants={Text}
          initial="rest"
          animate={active ? 'animate' : undefined}
        >
          {active
            ? activeText
              ? activeText
              : 'On'
            : inactiveText
            ? inactiveText
            : 'Off'}
        </SwitchTypography>
      </SwitchContainer>
    </SwitchWrapper>
  );
};

export default Switch;

const SwitchWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const SwitchLabel = styled(Typography)`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.h4};
`;

const SwitchContainer = styled.div<{ active?: boolean; isBig?: boolean }>`
  cursor: pointer;
  position: relative;
  min-width: 50px;
  height: 24px;
  padding: 4px 6px 4px 4px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.colors.inputBackgroundAlternate};

  ${({ active }) =>
    active
      ? css`
          padding: 4px 4px 4px 6px;

          &.green-bg {
            background-color: ${({ theme }) => theme.colors.green};
          }
        `
      : null}
  ${({ isBig }) =>
    isBig
      ? css`
          min-width: 62px;
          height: 32px;
          padding: 5px 10px 5px 5px;
        `
      : null}
  ${({ isBig, active }) =>
    isBig && active
      ? css`
          padding: 5px 5px 5px 10px;
        `
      : null}
`;

const SwitchItem = styled(motion.span)<{
  active?: boolean;
  isGreen?: boolean;
  isBig?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  box-shadow: none;
  z-index: 20;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.curve}`};

  ${({ isBig }) =>
    isBig
      ? css`
          height: 22px;
          width: 22px;
        `
      : null}

  ${({ active }) =>
    active
      ? css`
          background-color: ${({ theme }) => theme.colors.accent};
        `
      : null}

  ${({ active, isGreen }) =>
    active && isGreen
      ? css`
          background-color: ${({ theme }) => theme.colors.greenLight};
        `
      : null}
`;

const SwitchTypography = styled(motion.span)<{
  active?: boolean;
}>`
  position: absolute;
  top: 50%;
  right: 6px;
  left: auto;
  transform: translate(0, -50%);
  z-index: 10;
  text-transform: uppercase;
  opacity: 0.5;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  line-height: 12px;
  color: ${({ theme }) => theme.colors.primary};

  ${({ active }) =>
    active
      ? css`
          &.green-text {
            color: ${({ theme }) => theme.colors.greenLight};
          }
        `
      : null}
`;
